import { PopupComponentProps, TriggerProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import Popup from '@databases/atlaskit/Popup';
import { defaultStoreUtils } from '@databases/redux';
import { deselectAll } from '@databases/redux/cellSelections/actions';
import useHandleOutsideClick from '@databases/utils/hooks/useHandleOutsideClick';
import React, {
  forwardRef,
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { OpenThread } from '../context/CommentContext';
import useCommentContext from '../hooks/useCommentContext';

import CellCommentsButton from './CellCommentsButton';
import { Thread } from './CommentThread';
import CommentThreads from './CommentThreads';

const containerStyles = xcss({
  zIndex: 'dialog',
});

// eslint-disable-next-line react/display-name
const CustomPopupContainer = forwardRef<HTMLDivElement, PopupComponentProps>(
  ({ children, 'data-testid': testId, ...props }, ref) => (
    <Box xcss={containerStyles} testId={testId} {...props} ref={ref}>
      {children}
    </Box>
  )
);

interface ThreadPopupProps {
  contentId: string;
  entryId: string;
  fieldId: string;
  valueId: string;
  threads: Thread[] | undefined;
  totalCommentCount: number;
  hasThreads: boolean;
  shouldShowHoverButton: boolean;
  openThread: OpenThread;
  toggleComment: (e: React.MouseEvent) => void;
  setIsThreadPopupHovered: (isHovered: boolean) => void;
  placement?: 'right-start' | 'left-start' | 'bottom-end' | 'auto';
  panel?: boolean;
  setIsHoverCell?: (isHovered: boolean) => void;
}

const ThreadPopup: FunctionComponent<ThreadPopupProps> = ({
  contentId,
  entryId,
  fieldId,
  valueId,
  threads,
  totalCommentCount,
  hasThreads,
  shouldShowHoverButton,
  openThread,
  toggleComment,
  setIsThreadPopupHovered,
  placement = 'right-start',
  panel = false,
  setIsHoverCell,
}) => {
  const { setOpenThread, setFocusedCommentContentId } = useCommentContext();
  const dispatch = defaultStoreUtils.useDispatch();
  const commentThreadsPopupRef = useRef<HTMLDivElement>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const commentToggled = useMemo(
    () => openThread.valueId === valueId && !!openThread.panel === panel,
    [openThread.panel, openThread.valueId, panel, valueId]
  );

  useEffect(() => {
    if (!commentToggled) {
      setIsThreadPopupHovered(false);
    }
  }, [commentToggled, setIsThreadPopupHovered]);

  const handleClose = useCallback(() => {
    setOpenThread({ valueId: undefined });
    setFocusedCommentContentId(undefined);
    setIsHoverCell?.(false);
    dispatch(deselectAll());
  }, [dispatch, setFocusedCommentContentId, setIsHoverCell, setOpenThread]);

  // Since hook is mounted on every cell, we only want to call the close method when the ref is valid and user is not in delete dialog
  useHandleOutsideClick(
    handleClose,
    commentThreadsPopupRef,
    [
      '[aria-roledescription="emoji button"]',
      `#cell-view-thread-button-${valueId}`,
      `#cell-add-thread-button-${valueId}`,
    ], // ignore clicks on emoji button in "more emojis" menu
    commentToggled && !isDeleting
  );

  return (
    <Popup
      shouldRenderToParent={true}
      isOpen={commentToggled}
      placement={placement}
      autoFocus={false}
      boundary={document.getElementById('layout-table') ?? 'clippingParents'}
      popupComponent={CustomPopupContainer as FunctionComponent<PopupComponentProps>}
      offset={panel ? [24, 0] : [-18, -12]}
      trigger={(triggerProps?: TriggerProps) =>
        hasThreads || shouldShowHoverButton ? (
          <Box
            ref={triggerProps?.ref}
            id={`cell-comment-button-${valueId}`}
            xcss={xcss({
              padding: 'space.0',
              position: 'absolute',
              right: 'space.100',
              top: 'space.150',
            })}
          >
            <CellCommentsButton
              onClick={toggleComment}
              commentToggled={commentToggled}
              hasThreads={hasThreads}
              commentCount={totalCommentCount}
              shouldShowHoverButton={shouldShowHoverButton}
              valueId={valueId}
            />
          </Box>
        ) : null
      }
      content={() => (
        <div
          onMouseEnter={() => setIsThreadPopupHovered(true)}
          onMouseLeave={() => setIsThreadPopupHovered(false)}
          id="comment-threads-popup"
          ref={commentThreadsPopupRef}
        >
          <CommentThreads
            contentId={contentId}
            entryId={entryId}
            fieldId={fieldId}
            commentToggled={commentToggled}
            threads={threads}
            openThread={openThread}
            create={openThread.create}
            setIsDeleting={setIsDeleting}
            handleClose={handleClose}
          />
        </div>
      )}
    />
  );
};

export default ThreadPopup;
